import React from 'react';

import { Box, Button, FormHelperText } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	Control,
	useFieldArray,
	FormState,
	UseFormClearErrors,
	UseFormGetValues,
} from 'react-hook-form';
import SingleDeliveryPeriod from './SingleDeliveryPeriod';
import { useTranslation } from 'services/localization/useTranslation';
import { getDefaultDeliveryPeriod, DeliveryOptionForm } from '../utils';

interface Props {
	control: Control<DeliveryOptionForm>;
	errors: FormState<DeliveryOptionForm>['errors'];
	clearErrors: UseFormClearErrors<DeliveryOptionForm>;
	getValues: UseFormGetValues<DeliveryOptionForm>;
}

const DeliveryPeriods = (props: Props) => {
	const { control, errors, clearErrors, getValues } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'deliveryPeriods',
	});

	const handleAddDeliveryPeriod = () => {
		const deliveryPeriodEndDates = getValues().deliveryPeriods?.map((period) => period.endDate);
		const latestEndDate = deliveryPeriodEndDates
			? deliveryPeriodEndDates[deliveryPeriodEndDates.length - 1]
			: null;
		append(getDefaultDeliveryPeriod(latestEndDate));

		if (!!errors.deliveryPeriods && errors.deliveryPeriods.type === 'required') {
			clearErrors('deliveryPeriods');
		}
	};

	return (
		<Box>
			{!!errors.deliveryPeriods && errors.deliveryPeriods.message && (
				<FormHelperText error data-error="true">
					{errors.deliveryPeriods.message}
				</FormHelperText>
			)}
			{fields.map((item, index) => (
				<Box key={item.id} mt={3}>
					<SingleDeliveryPeriod
						index={index}
						value={item}
						isLastPeriod={index === fields.length - 1}
						onDelete={() => remove(index)}
						handleAddDeliveryPeriod={handleAddDeliveryPeriod}
						getValues={getValues}
					/>
				</Box>
			))}
			{!fields.length && (
				<Box mt={3}>
					<Button
						variant="outlined"
						color="primary"
						className={classes.button}
						onClick={handleAddDeliveryPeriod}
					>{`+ ${t('delivery.addPeriod', 'Add period')}`}</Button>
				</Box>
			)}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		button: {
			minHeight: theme.spacing(4),
			maxHeight: theme.spacing(4),
		},
	}),
);

export default DeliveryPeriods;
